import React, { useState, useEffect } from 'react';
import api from '../Service/api';
import './EmployeeAuthorityManager.css';

const EmployeeAuthorityManager = () => {
  // State management
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [availableAuthorities, setAvailableAuthorities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedAuthorityIds, setSelectedAuthorityIds] = useState([]);
  const [currentAuthorities, setCurrentAuthorities] = useState([]);
  const [expandedMenus, setExpandedMenus] = useState({});

  // Authority Hierarchy Structure (same as CreateEmployee)
  const authorityHierarchy = {
    "HOME": { label: "Home", icon: "🏠", children: {} },
    
    "PURCHASES": {
      label: "Purchases",
      icon: "🛒",
      children: {
        "ADD_PURCHASE": {
          label: "Add Purchase",
          children: {
            "ENTER_PURCHASE": { label: "Enter Purchase" },
            "VIEW_PURCHASE": { label: "View Purchase" }
          }
        },
        "RETURN_PURCHASE": {
          label: "Return Purchase",
          children: {
            "ENTER_PURCHASE_RETURN": { label: "Enter Return" },
            "VIEW_PURCHASE_RETURN": { label: "View Return" }
          }
        }
      }
    },

    "SALES": {
      label: "Sales",
      icon: "💰",
      children: {
        "ADD_SALE": {
          label: "Add Sale",
          children: {
            "ENTER_SALE": { label: "Enter Sale" },
            "VIEW_SALE": { label: "View Sale" },
            "FACTORY_USE": { label: "Enter Factory Use" },
            "VIEW_FACTORY_USE": { label: "View Factory Use" },
            "VIEW_DELIVERY_CHALAN": { label: "View Delivery Chalan" }
          }
        },
        "RETURN_SALE": {
          label: "Return Sale",
          children: {
            "ENTER_SALE_RETURN": { label: "Enter Return" },
            "VIEW_SALE_RETURN": { label: "View Return" }
          }
        }
      }
    },

    "CATEGORIES": {
      label: "Categories",
      icon: "📋",
      children: {
        "PRODUCT": {
          label: "Product Categories",
          children: {
            "ADD_CATEGORY": { label: "Add Category" },
            "VIEW_CATEGORIES": { label: "View Categories" }
          }
        },
        "EXPENSE": {
          label: "Expense Categories",
          children: {
            "ADD & VIEW": { label: "Add & View" }
          }
        }
      }
    },

    "VENDORS&CUSTOMERS": {
      label: "Vendors & Customers",
      icon: "👥",
      children: {
        "VENDORS": {
          label: "Vendors",
          children: {
            "ADD_VENDOR": { label: "Add Vendor" },
            "VIEW_VENDORS": { label: "View Vendors" }
          }
        },
        "CUSTOMERS": {
          label: "Customers",
          children: {
            "ADD_CUSTOMER": { label: "Add Customer" },
            "VIEW_CUSTOMERS": { label: "View Customers" }
          }
        }
      }
    },

    "PAY BILLS": {
      label: "Pay Bills",
      icon: "💵",
      children: {
        "VENDOR_BILL": {
          label: "Vendor Bill",
          children: {
            "PAY_VENDOR_BILL": { label: "Pay Bill" },
            "VIEW_VENDOR_BILLS": { label: "View Bill" }
          }
        },
        "CUSTOMER_BILL": {
          label: "Customer Bill",
          children: {
            "RECEIVE_CUSTOMER_BILL": { label: "Receive Bill" },
            "VIEW_CUSTOMER_BILLS": { label: "View Bill" }
          }
        }
      }
    },

    "EXPENSES": {
      label: "Expenses",
      icon: "💸",
      children: {
        "ADD_EXPENSE": { label: "Add Expense" },
        "VIEW_EXPENSES": { label: "View Expenses" }
      }
    },

    "EMPLOYEES": {
      label: "Employees",
      icon: "👔",
      children: {
        "ADD_EMPLOYEE_DETAILS": { label: "Add Details" },
        "VIEW_EMPLOYEES": { label: "View Details" },
        "PAY_SALARY": { label: "Pay Salaries" },
        "VIEW_SALARIES": { label: "View Salaries" }
      }
    },

    "CLIENT REQUIREMENTS": {
      label: "Client Requirements",
      icon: "📝",
      children: {
        "ADD_REQUIREMENT": { label: "Add Requirement" },
        "VIEW_REQUIREMENTS": { label: "View Requirements" }
      }
    },

    "REPORTS": {
      label: "Reports",
      icon: "📊",
      children: {
        "STOCK_INVENTORY": { label: "Stock Inventory" },
        "CUSTOMER_LEDGER": { label: "Customer Ledger" },
        "VENDOR_LEDGER": { label: "Vendor Ledger" },
        "DAILY_INVOICE": { label: "Daily Invoice" }
      }
    },

    "PRODUCTION": {
      label: "Production",
      icon: "🏭",
      children: {
        "OWNER PRODUCTION": { label: "Add Product & Steps" },
        "EMPLOYEE_PRODUCTION": { label: "Employee Production Time" },
        "OWNER DASHBOARD": { label: "Production Report" }
      }
    },

    "ORDERS": {
      label: "Orders",
      icon: "📦",
      children: {
        "CREATE_ORDER": { label: "Create Order" },
        "VIEW_ORDERS": { label: "View Orders" }
      }
    },

    "EMPLOYEE LOGIN": {
      label: "Employee Login Management",
      icon: "🔐",
      children: {
        "CREATE_EMPLOYEE_LOGIN": { label: "Create Employee" },
        "MANAGE_AUTHORITIES": { label: "Manage Authorities" }
      }
    }
  };

  // Fetch all employees with their authorities
  const fetchEmployeesWithAuthorities = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('📡 Making API call to: /employee-login/getUserNameAndAuthorities');
      const response = await api.get('/employee-login/getUserNameAndAuthorities');
      console.log('✅ Raw API Response:', response);
      
      if (response && response.data) {
        const apiData = response.data;
        console.log('📊 API Data:', apiData);
        
        if (apiData.code === 1) {
          if (apiData.additionalDetail && Array.isArray(apiData.additionalDetail)) {
            console.log('✅ Found employees in apiData.additionalDetail:', apiData.additionalDetail);
            
            // Transform the data to handle the new format
            const transformedEmployees = apiData.additionalDetail.map(emp => ({
              ...emp,
              authorities: emp.authorities || [],
              authorityNames: Array.isArray(emp.authorities) 
                ? emp.authorities.map(auth => {
                    if (typeof auth === 'object') {
                      return auth.name || auth.authorityName || '';
                    }
                    return auth.toString();
                  })
                : [],
              authorityIds: Array.isArray(emp.authorities)
                ? emp.authorities.map(auth => {
                    if (typeof auth === 'object') {
                      return auth.id || auth.authorityId || 0;
                    }
                    return 0;
                  }).filter(id => id !== 0)
                : []
            }));
            
            setEmployees(transformedEmployees);
            
            // Check if selected employee still exists in the new list
            if (selectedEmployee) {
              const employeeStillExists = transformedEmployees.some(
                emp => emp.employeeId === selectedEmployee.employeeId
              );
              
              if (!employeeStillExists) {
                setSelectedEmployee(null);
                setCurrentAuthorities([]);
                setSelectedAuthorityIds([]);
                console.log('🔄 Cleared selection for deleted employee');
              }
            }
          } else {
            console.log('❌ No additionalDetail array found in apiData');
            setError('No employee data found in response');
          }
        } else {
          setError(apiData.message || 'API request failed');
        }
      } else {
        setError('No data received from server');
      }
    } catch (err) {
      console.error('❌ Fetch error:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Unknown error';
      setError('Failed to fetch employees: ' + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Fetch available authorities
  const fetchAvailableAuthorities = async () => {
    try {
      console.log('📡 Fetching available authorities...');
      const response = await api.get('/employee-login/getAuthorities');
      console.log('✅ Authorities response:', response);
      
      if (response && response.data) {
        const apiData = response.data;
        
        if (apiData.code === 1 && apiData.additionalDetail && Array.isArray(apiData.additionalDetail)) {
          console.log('✅ Raw authorities data:', apiData.additionalDetail);
          
          const transformedAuthorities = apiData.additionalDetail.map(auth => ({
            id: auth.authorityId,
            name: auth.authorityName,
            description: auth.authorityDescription || `${auth.authorityName} permission`
          }));
          
          console.log('✅ Transformed authorities:', transformedAuthorities);
          setAvailableAuthorities(transformedAuthorities);
          return;
        }
      }
      
      extractAuthoritiesFromEmployees();
    } catch (err) {
      console.error('❌ Failed to fetch authorities:', err);
      extractAuthoritiesFromEmployees();
    }
  };

  // Extract unique authorities from employees
  const extractAuthoritiesFromEmployees = () => {
    if (employees.length === 0) {
      setAvailableAuthorities([]);
      return;
    }
    
    const allAuthorities = new Set();
    employees.forEach(emp => {
      if (emp.authorities && Array.isArray(emp.authorities)) {
        emp.authorities.forEach(auth => {
          if (typeof auth === 'object') {
            allAuthorities.add(JSON.stringify(auth));
          } else if (typeof auth === 'string') {
            allAuthorities.add(auth);
          }
        });
      }
    });
    
    const authorityList = Array.from(allAuthorities).map((authStr, index) => {
      if (authStr.startsWith('{')) {
        try {
          const authObj = JSON.parse(authStr);
          return {
            id: authObj.id || authObj.authorityId || index + 1,
            name: authObj.name || authObj.authorityName || `Authority ${index + 1}`,
            description: authObj.description || authObj.authorityDescription || 
                        `${authObj.name || authObj.authorityName || `Authority ${index + 1}`} permission`
          };
        } catch (e) {
          console.error('Error parsing authority object:', e);
          return {
            id: index + 1,
            name: authStr,
            description: `${authStr} permission`
          };
        }
      } else {
        return {
          id: index + 1,
          name: authStr,
          description: `${authStr} permission`
        };
      }
    });
    
    console.log('📋 Extracted authorities from employees:', authorityList);
    setAvailableAuthorities(authorityList);
  };

  // Handle employee selection
  const handleEmployeeSelect = (employee) => {
    console.log('👤 Selected employee:', employee);
    setSelectedEmployee(employee);
    
    const authoritiesArray = employee.authorities || [];
    const ids = authoritiesArray.map(auth => {
      if (typeof auth === 'object') {
        return auth.id || auth.authorityId || 0;
      }
      return 0;
    }).filter(id => id !== 0);
    
    console.log('👤 Extracted IDs:', ids);
    
    setSelectedAuthorityIds(ids);
    setCurrentAuthorities(authoritiesArray);
    setSuccess('');
    setError('');
    setExpandedMenus({}); // Reset expanded menus
  };

  // Toggle menu expansion
  const toggleMenu = (menuPath) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuPath]: !prev[menuPath]
    }));
  };

  // Handle authority selection in tree
  const handleAuthorityToggle = (authorityId) => {
    setSelectedAuthorityIds(prev => {
      const newIds = prev.includes(authorityId)
        ? prev.filter(id => id !== authorityId)
        : [...prev, authorityId];
      console.log('🔘 New selected authority IDs:', newIds);
      return newIds;
    });
  };

  // Helper to get authority name
  const getAuthorityName = (authority) => {
    if (!authority) return '';
    if (typeof authority === 'object') {
      return authority.name || authority.authorityName || '';
    }
    return authority.toString();
  };

  // Helper to get authority ID
  const getAuthorityId = (authority) => {
    if (!authority) return 0;
    if (typeof authority === 'object') {
      return authority.id || authority.authorityId || 0;
    }
    return 0;
  };

  // Check if authority is selected by ID
  const isAuthoritySelected = (authorityId) => {
    return selectedAuthorityIds.includes(authorityId);
  };

  // Recursive function to render authority tree
  const renderAuthorityTree = (node, parentPath = "") => {
    if (!node) return null;

    return Object.entries(node).map(([key, value]) => {
      const currentPath = parentPath ? `${parentPath}.${key}` : key;
      const hasChildren = value.children && Object.keys(value.children).length > 0;
      const isExpanded = expandedMenus[currentPath];
      
      // Find the actual authority object from availableAuthorities
      const authorityObj = availableAuthorities.find(a => 
        a.name?.toUpperCase() === key.toUpperCase()
      );
      const authorityId = authorityObj?.id;
      const isAvailable = !!authorityId;
      const isSelected = authorityId ? selectedAuthorityIds.includes(authorityId) : false;

      // Don't render if authority doesn't exist and has no children
      if (!isAvailable && !hasChildren) return null;

      return (
        <div key={key} className="authority-tree-item">
          <div 
            className={`authority-row ${hasChildren ? 'has-children' : ''} ${isSelected ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}`}
            style={{ paddingLeft: `${parentPath.split('.').filter(p => p).length * 20}px` }}
          >
            {hasChildren && (
              <span 
                className="expand-icon" 
                onClick={() => toggleMenu(currentPath)}
              >
                {isExpanded ? '▼' : '►'}
              </span>
            )}
            
            {!hasChildren && <span className="expand-icon-placeholder"></span>}
            
            {isAvailable && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleAuthorityToggle(authorityId)}
                id={`auth-${key}`}
                disabled={!isAvailable}
              />
            )}
            
            {!isAvailable && <span className="checkbox-placeholder"></span>}
            
            <label 
              htmlFor={`auth-${key}`}
              className={`authority-label ${!isAvailable ? 'text-muted' : ''}`}
              onClick={(e) => {
                if (hasChildren && e.target.tagName !== 'INPUT') {
                  toggleMenu(currentPath);
                }
              }}
            >
              <span className="authority-icon">{value.icon}</span>
              {value.label || key}
              {!isAvailable && <span className="not-available-badge">(not available)</span>}
              {isAvailable && authorityId && (
                <span className="authority-id-badge">ID: {authorityId}</span>
              )}
            </label>
          </div>

          {hasChildren && isExpanded && (
            <div className="authority-children">
              {renderAuthorityTree(value.children, currentPath)}
            </div>
          )}
        </div>
      );
    });
  };

  // Update employee authorities
  const handleUpdateAuthorities = async () => {
    if (!selectedEmployee) {
      setError('Please select an employee first');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      console.log('🔄 Updating authorities for employee:', selectedEmployee.employeeId);
      console.log('📋 Selected authority IDs:', selectedAuthorityIds);
      
      const response = await api.put(
        `/employee-login/employee/${selectedEmployee.employeeId}`,
        { authorityIds: selectedAuthorityIds }
      );
      
      console.log('✅ Update response:', response);
      
      if (response && response.data) {
        const apiData = response.data;
        
        if (apiData.code === 1) {
          setSuccess('Authorities updated successfully!');
          fetchEmployeesWithAuthorities();
          
          // Update current authorities after successful update
          const updatedCurrentAuthorities = selectedAuthorityIds.map(id => {
            const auth = availableAuthorities.find(a => a.id === id);
            if (auth) {
              return { name: auth.name, id: auth.id };
            }
            return { name: `Authority ${id}`, id: id };
          });
          setCurrentAuthorities(updatedCurrentAuthorities);
        } else {
          setError(apiData.message || 'Failed to update authorities');
        }
      } else {
        setError('Invalid response from server');
      }
    } catch (err) {
      console.error('❌ Update error:', err);
      setError('Update failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Delete specific authorities
  const handleDeleteAuthorities = async (authoritiesToDelete) => {
    if (!selectedEmployee || authoritiesToDelete.length === 0) {
      setError('Please select an employee and authorities to delete');
      return;
    }

    if (!window.confirm(`Remove ${authoritiesToDelete.length} authority(ies) from ${selectedEmployee.username}?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log('🗑️ Deleting authorities:', authoritiesToDelete);
      
      const authorityIdsToDelete = authoritiesToDelete.map(authName => {
        const auth = currentAuthorities.find(a => 
          getAuthorityName(a) === authName
        );
        return getAuthorityId(auth);
      }).filter(id => id !== 0);
      
      console.log('🗑️ Authority IDs to delete:', authorityIdsToDelete);
      
      const response = await api.delete(
        `/employee-login/employee/${selectedEmployee.employeeId}/specific`,
        {
          data: { authorityIds: authorityIdsToDelete }
        }
      );
      
      console.log('✅ Delete specific response:', response);
      
      if (response && response.data) {
        const apiData = response.data;
        
        if (apiData.code === 1) {
          setSuccess(`Successfully removed ${authoritiesToDelete.length} authority(ies)!`);
          fetchEmployeesWithAuthorities();
          
          const remainingIds = selectedAuthorityIds.filter(id => 
            !authorityIdsToDelete.includes(id)
          );
          
          setSelectedAuthorityIds(remainingIds);
          
          const newCurrentAuthorities = currentAuthorities.filter(auth => 
            !authoritiesToDelete.includes(getAuthorityName(auth))
          );
          setCurrentAuthorities(newCurrentAuthorities);
          
          if (selectedEmployee) {
            setSelectedEmployee({
              ...selectedEmployee,
              authorities: newCurrentAuthorities
            });
          }
        } else {
          setError(apiData.message || 'Failed to remove authorities');
        }
      } else {
        setError('Invalid response from server');
      }
    } catch (err) {
      console.error('❌ Delete specific error:', err);
      setError('Delete failed: ' + (err.response?.data?.message || err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Delete all authorities for selected employee
  const handleDeleteAllAuthorities = async () => {
    if (!selectedEmployee) {
      setError('Please select an employee first');
      return;
    }

    if (!window.confirm(`Remove ALL authorities from ${selectedEmployee.username}? This cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log('🗑️ Deleting all authorities for:', selectedEmployee.employeeId);
      
      const allAuthorityIds = currentAuthorities.map(auth => 
        getAuthorityId(auth)
      ).filter(id => id !== 0) || [];
      
      console.log('🗑️ All authority IDs to delete:', allAuthorityIds);
      
      const response = await api.delete(
        `/employee-login/employee/${selectedEmployee.employeeId}/specific`,
        {
          data: { authorityIds: allAuthorityIds }
        }
      );
      
      console.log('✅ Delete all response:', response);
      
      if (response && response.data) {
        const apiData = response.data;
        
        if (apiData.code === 1) {
          setSuccess('All authorities removed successfully!');
          fetchEmployeesWithAuthorities();
          setSelectedAuthorityIds([]);
          setCurrentAuthorities([]);
          
          if (selectedEmployee) {
            setSelectedEmployee({
              ...selectedEmployee,
              authorities: []
            });
          }
        } else {
          setError(apiData.message || 'Failed to remove authorities');
        }
      } else {
        setError('Invalid response from server');
      }
    } catch (err) {
      console.error('❌ Delete all error:', err);
      setError('Delete failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Delete employee user
  const handleDeleteEmployee = async () => {
    if (!selectedEmployee) {
      setError('Please select an employee first');
      return;
    }

    const phoneNumber = selectedEmployee.phoneNumber;
    const employeeNameToDelete = selectedEmployee.username;
    const employeeIdToDelete = selectedEmployee.employeeId;
    
    if (!phoneNumber || phoneNumber.trim() === '') {
      setError('Selected employee does not have a valid phone number');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete employee ${employeeNameToDelete} (Phone: ${phoneNumber})?\n\nThis will:\n1. Deactivate their authentication record\n2. Deactivate their login record\n3. Remove all their authorities\n\nThis action cannot be undone!`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log('🗑️ Deleting employee user with phone:', phoneNumber);
      
      const response = await api.delete(
        `/employee-login/delete-login/${phoneNumber}`
      );
      
      console.log('✅ Delete employee response:', response);
      
      if (response && response.data) {
        const apiData = response.data;
        
        if (apiData.code === 1) {
          setEmployees(prevEmployees => 
            prevEmployees.filter(emp => emp.employeeId !== employeeIdToDelete)
          );
          
          setSelectedEmployee(null);
          setCurrentAuthorities([]);
          setSelectedAuthorityIds([]);
          
          setSuccess(`Employee ${employeeNameToDelete} deleted successfully!`);
          
          setTimeout(() => {
            fetchEmployeesWithAuthorities();
          }, 100);
        } else {
          setError(apiData.message || 'Failed to delete employee');
        }
      } else {
        setError('Invalid response from server');
      }
    } catch (err) {
      console.error('❌ Delete employee error:', err);
      setError('Delete failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Initialize on component mount
  useEffect(() => {
    fetchEmployeesWithAuthorities();
  }, []);

  // Fetch authorities after employees are loaded
  useEffect(() => {
    if (employees.length > 0) {
      fetchAvailableAuthorities();
    }
  }, [employees]);

  return (
    <div className="employee-authority-manager">
      <h2>Employee Authority Management</h2>
      
      {/* Status Messages */}
      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError('')} className="close-btn">×</button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="close-btn">×</button>
        </div>
      )}

      {/* Loading Indicator */}
      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <span>Processing...</span>
        </div>
      )}

      <div className="manager-container">
        {/* Left Panel: Employee List */}
        <div className="employee-list-panel">
          <div className="panel-header">
            <h3>Employees ({employees.length})</h3>
            <button 
              onClick={fetchEmployeesWithAuthorities}
              className="btn btn-sm btn-refresh"
              title="Refresh employee list"
              disabled={loading}
            >
              ↻ Refresh
            </button>
          </div>
          <div className="employee-list">
            {employees.length === 0 && !loading ? (
              <div className="empty-state">
                <p>No employees found</p>
                <button 
                  onClick={fetchEmployeesWithAuthorities}
                  className="btn btn-sm"
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Try Again'}
                </button>
              </div>
            ) : (
              employees.map((employee, index) => (
                <div 
                  key={employee.employeeId || `emp-${index}`}
                  className={`employee-card ${selectedEmployee?.employeeId === employee.employeeId ? 'selected' : ''}`}
                  onClick={() => handleEmployeeSelect(employee)}
                >
                  <div className="employee-info">
                    <div className="employee-name">{employee.username || 'Unknown User'}</div>
                    <div className="employee-details">
                      <div className="employee-id">ID: {employee.employeeId || 'N/A'}</div>
                      <div className="employee-phone">
                        📱 {employee.phoneNumber || 'No phone'}
                      </div>
                    </div>
                  </div>
                  <div className="authority-count">
                    {(employee.authorities?.length || 0)} authority(s)
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Panel: Authority Management */}
        <div className="authority-management-panel">
          {selectedEmployee ? (
            <>
              <div className="selected-employee-header">
                <div className="employee-header-top">
                  <h3>Managing: {selectedEmployee.username}</h3>
                  <button 
                    onClick={handleDeleteEmployee}
                    className="btn btn-danger btn-sm"
                    disabled={loading || !selectedEmployee.phoneNumber}
                    title="Delete employee user"
                  >
                    🗑️ Delete Employee
                  </button>
                </div>
                <div className="employee-details">
                  <span>Employee ID: {selectedEmployee.employeeId}</span>
                  {selectedEmployee.phoneNumber && (
                    <span>Phone: {selectedEmployee.phoneNumber}</span>
                  )}
                  <span className="current-authorities-count">
                    Current Authorities: {currentAuthorities.length}
                  </span>
                </div>
              </div>

              {/* Current Authorities */}
              <div className="current-authorities-section">
                <h4>Current Authorities ({currentAuthorities.length})</h4>
                {currentAuthorities.length > 0 ? (
                  <div className="authorities-list">
                    {currentAuthorities.map((authority, index) => (
                      <div key={`current-auth-${getAuthorityId(authority)}-${index}`} className="authority-tag">
                        <span className="authority-name-display">
                          {getAuthorityName(authority)}
                          {getAuthorityId(authority) > 0 && (
                            <span className="authority-id"> (ID: {getAuthorityId(authority)})</span>
                          )}
                        </span>
                        <button 
                          onClick={() => handleDeleteAuthorities([getAuthorityName(authority)])}
                          className="remove-btn"
                          title="Remove this authority"
                          disabled={loading}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    
                    {currentAuthorities.length > 0 && (
                      <button 
                        onClick={handleDeleteAllAuthorities}
                        className="btn btn-danger btn-sm remove-all-btn"
                        disabled={loading}
                      >
                        Remove All
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="empty-state">No authorities assigned</div>
                )}
              </div>

              {/* Authority Selection Tree */}
              <div className="authority-selection-section">
                <h4>Available Authorities ({availableAuthorities.length})</h4>
                {availableAuthorities.length === 0 ? (
                  <div className="empty-state">
                    <p>No authorities available</p>
                    <button 
                      onClick={() => {
                        console.log('Current employees:', employees);
                        console.log('Current availableAuthorities:', availableAuthorities);
                        extractAuthoritiesFromEmployees();
                      }}
                      className="btn btn-sm"
                      disabled={loading}
                    >
                      Load from employees
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="authority-tree-container">
                      <div className="tree-scroll">
                        {renderAuthorityTree(authorityHierarchy)}
                      </div>
                    </div>
                    
                    <div className="debug-info-small">
                      <small>Showing {availableAuthorities.length} authorities in tree.</small>
                    </div>
                  </>
                )}
              </div>

              {/* Selected Summary */}
              <div className="selected-summary">
                <h4>Selected Authorities: {selectedAuthorityIds.length}</h4>
                <div className="selected-tags">
                  {selectedAuthorityIds.map(id => {
                    const auth = availableAuthorities.find(a => a.id === id);
                    return auth && (
                      <span key={id} className="selected-tag">
                        {auth.name}
                        <button onClick={() => handleAuthorityToggle(id)}>×</button>
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button 
                  onClick={handleUpdateAuthorities}
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Update Authorities'}
                </button>
                <button 
                  onClick={() => {
                    const ids = currentAuthorities.map(auth => getAuthorityId(auth)) || [];
                    setSelectedAuthorityIds(ids);
                    setSuccess('');
                    setError('');
                  }}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  Reset to Current
                </button>
              </div>

              {/* Quick Stats */}
              <div className="quick-stats">
                <div className="stat-item">
                  <span className="stat-label">Selected IDs:</span>
                  <span className="stat-value">
                    {selectedAuthorityIds.length > 0 
                      ? selectedAuthorityIds.join(', ') 
                      : 'None selected'}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="select-employee-prompt">
              <h4>Select an employee to manage their authorities</h4>
              <p>Click on an employee from the list on the left to begin</p>
              {employees.length === 0 && !loading && (
                <div className="help-section">
                  <p className="warning">⚠️ No employees loaded.</p>
                  <p>Please check the browser console (F12) for detailed error information.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeAuthorityManager;